import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { Input } from "../../../../../../_metronic/_partials/controls";
import * as usersCrud from "../../../_redux/users/usersCrud";
import * as businessesCrud from "../../../_redux/businesses/businessesCrud";
import * as categoriesCrud from "../../../_redux/categories/categoriesCrud";
import * as countriesCrud from "../../../_redux/countries/countriesCrud";
import { STANDARD_LANGUAGES } from "../../../languages";
import { AddCategoryModal } from "../../categories/AddCategoryModal";

// Validation schema
const BusinessEditSchema = Yup.object().shape({
  userid: Yup.string().required("User is required"),
  name: Yup.string()
    .min(2, "Minimum 2 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Name is required"),
  business_email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  phone: Yup.string().required("Phone is required"),
  area_code: Yup.string().required("Area code is required"),
  latitude: Yup.number()
    .typeError("Latitude must be a number")
    .required("Latitude is required"),
  longitude: Yup.number()
    .typeError("Longitude must be a number")
    .required("Longitude is required"),
  tagline: Yup.string().required("Short Description is required"),
  address: Yup.string().required("Address is required"),
});

export function CreateBusinessForm({
  business,
  btnRef,
  saveBusiness,
}) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedOriginCountries, setSelectedOriginCountries] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [categoryModalInitialName, setCategoryModalInitialName] = useState("");

  const handleCategoryFromModal = ({ id, name }) => {
    if (!id) return;
    setCategories((prev) => {
      if (prev.some((c) => String(c.id) === String(id))) return prev;
      return [
        ...prev,
        { id: String(id), name, status: "active", approval_status: "approved" },
      ];
    });
    setSelectedCategories((prev) => [...new Set([...prev, String(id)])]);
  };

  const [addressSearchText, setAddressSearchText] = useState('');
  const [addressSearchResults, setAddressSearchResults] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [addressSearchError, setAddressSearchError] = useState('');

  const handleAddressSearch = async (searchTerm) => {
    const term = searchTerm || addressSearchText;
    if (!term || !term.trim()) return;
    setIsSearchingAddress(true);
    setAddressSearchError('');
    try {
      const res = await businessesCrud.geocodeAddress(term.trim());
      if (res.data && res.data.status && res.data.results && res.data.results.length > 0) {
        setAddressSearchResults(res.data.results);
      } else {
        setAddressSearchResults([]);
        setAddressSearchError(res.data?.message || 'No address matches found.');
      }
    } catch (err) {
      console.error('Error searching address:', err);
      setAddressSearchError('Failed to search address. Please try again.');
    } finally {
      setIsSearchingAddress(false);
    }
  };

  useEffect(() => {
    setLoadingUsers(true);
    usersCrud
      .findUsers({
        pageNumber: 1,
        pageSize: 1000,
        filter: {},
        sortField: "name",
        sortOrder: "asc",
      })
      .then((res) => {
        if (res.data && res.data.entities) {
          setUsers(res.data.entities);
        }
        setLoadingUsers(false);
      })
      .catch((err) => {
        console.error("Error fetching users:", err);
        setLoadingUsers(false);
      });

    categoriesCrud
      .findCategories({ pageNumber: 1, pageSize: 1000, filter: {}, sortField: "name", sortOrder: "asc" })
      .then((res) => {
        if (res.data && res.data.entities) setCategories(res.data.entities);
      })
      .catch((err) => console.error("Error fetching categories:", err));

    countriesCrud
      .getAllCountries()
      .then((res) => {
        if (res.data && res.data.data) setCountries(res.data.data);
      })
      .catch((err) => console.error("Error fetching countries:", err));
  }, []);

  const userOptions = users.map((u) => ({
    value: u.userid || u.id,
    label: `${u.name} (${u.email})`,
  }));

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={business}
        validationSchema={BusinessEditSchema}
        onSubmit={(values) => {
          const payload = {
            ...values,
            category_id: selectedCategories,
            origin_countries: selectedOriginCountries,
            languages: selectedLanguages
          };
          saveBusiness(payload);
        }}
      >
        {({ handleSubmit, setFieldValue, values, errors, touched }) => (
          <>
            <Form className="form form-label-right">
              {/* User Selection */}
              <div className="form-group row mb-6">
                <div className="col-lg-12">
                  <label className="font-weight-bold">Select Owner User</label>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    isLoading={loadingUsers}
                    isClearable={true}
                    isSearchable={true}
                    name="userid"
                    options={userOptions}
                    value={userOptions.find((opt) => opt.value === values.userid) || null}
                    onChange={(option) => {
                      const selectedUid = option ? option.value : "";
                      setFieldValue("userid", selectedUid);
                      if (option) {
                        const foundUser = users.find((u) => (u.userid || u.id) === selectedUid);
                        if (foundUser) {
                          if (foundUser.email) {
                            setFieldValue("business_email", foundUser.email);
                          }
                          if (foundUser.phone_number) {
                            setFieldValue("phone", foundUser.phone_number);
                          }
                          if (foundUser.name && (!values.name || values.name === "")) {
                            setFieldValue("name", foundUser.name);
                          }
                        }
                      }
                    }}
                    placeholder="Search and select owner user..."
                  />
                  {errors.userid && touched.userid ? (
                    <div className="text-danger mt-1">{errors.userid}</div>
                  ) : null}
                </div>
              </div>

              <div className="form-group row">
                <div className="col-lg-4">
                  <Field
                    name="name"
                    component={Input}
                    placeholder="Business Name"
                    label="Name"
                  />
                </div>

                <div className="col-lg-4">
                  <Field
                    type="email"
                    name="business_email"
                    component={Input}
                    placeholder="Business Email"
                    label="Business Email"
                  />
                </div>

                <div className="col-lg-4">
                  <Field
                    name="phone"
                    component={Input}
                    placeholder="Phone Number"
                    label="Phone"
                  />
                </div>
              </div>

              <div className="form-group row">
                <div className="col-lg-4">
                  <Field
                    name="area_code"
                    component={Input}
                    placeholder="Area Code"
                    label="Area code"
                  />
                </div>

                <div className="col-lg-4">
                  <Field
                    name="latitude"
                    component={Input}
                    placeholder="Latitude"
                    label="Latitude"
                  />
                </div>

                <div className="col-lg-4">
                  <Field
                    name="longitude"
                    component={Input}
                    placeholder="Longitude"
                    label="Longitude"
                  />
                </div>
              </div>

              {/* Address with Google Geocoding / Search */}
              <div className="card card-custom p-4 mb-6" style={{ backgroundColor: "#f8f9fa", border: "1px solid #ebedf3", borderRadius: "8px" }}>
                <div className="d-flex align-items-center mb-3">
                  <i className="fa fa-map-marked-alt text-primary mr-2 font-size-lg"></i>
                  <h6 className="font-weight-bold mb-0 text-dark">Address & Location Search (Google Geocoding)</h6>
                </div>
                <div className="form-group row mb-2">
                  <div className="col-lg-12">
                    <label className="font-weight-bold">Search Address on Google Maps to Auto-fill</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type address, place, or landmark to search on Google..."
                        value={addressSearchText}
                        onChange={(e) => setAddressSearchText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddressSearch();
                          }
                        }}
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-primary font-weight-bold"
                          disabled={isSearchingAddress || !addressSearchText.trim()}
                          onClick={() => handleAddressSearch()}
                        >
                          {isSearchingAddress ? (
                            <>
                              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                              Searching...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-search mr-1"></i> Search
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    <span className="form-text text-muted font-size-xs">
                      Search address to automatically populate formatted address, latitude, and longitude.
                    </span>

                    {addressSearchError && (
                      <div className="alert alert-custom alert-light-danger fade show p-2 mt-2 mb-0" role="alert">
                        <div className="alert-text font-size-xs">{addressSearchError}</div>
                      </div>
                    )}

                    {addressSearchResults && addressSearchResults.length > 0 && (
                      <div className="list-group mt-2 border rounded shadow-xs" style={{ maxHeight: "220px", overflowY: "auto", backgroundColor: "#fff" }}>
                        <div className="list-group-item bg-light-primary text-primary font-weight-bold font-size-xs py-2">
                          Found {addressSearchResults.length} Google Location Result(s) - Click one to apply:
                        </div>
                        {addressSearchResults.map((item, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="list-group-item list-group-item-action py-2 text-left d-flex justify-content-between align-items-center"
                            onClick={() => {
                              setFieldValue("address", item.formatted_address || "");
                              if (item.latitude !== null && item.latitude !== undefined) {
                                setFieldValue("latitude", item.latitude);
                              }
                              if (item.longitude !== null && item.longitude !== undefined) {
                                setFieldValue("longitude", item.longitude);
                              }
                              setAddressSearchResults([]);
                            }}
                          >
                            <div>
                              <div className="font-weight-bold font-size-sm text-dark">{item.formatted_address}</div>
                              <div className="text-muted font-size-xs">
                                Lat: {item.latitude}, Lng: {item.longitude} {item.city ? `• City: ${item.city}` : ''} {item.country ? `• Country: ${item.country}` : ''}
                              </div>
                            </div>
                            <span className="badge badge-light-success font-weight-bold ml-2">Apply</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group row mb-0">
                  <div className="col-lg-4">
                    <Field
                      name="tagline"
                      component={Input}
                      placeholder="Short Description"
                      label="Short Description"
                    />
                  </div>
                  <div className="col-lg-8">
                    <Field
                      name="address"
                      component={Input}
                      placeholder="Full Address"
                      label="Address"
                    />
                  </div>
                </div>
              </div>

              {/* Social Media Links Section */}
              <div className="card card-custom p-4 mb-6" style={{ backgroundColor: "#fdfdfd", border: "1px solid #e1e3ea", borderRadius: "8px" }}>
                <div className="d-flex align-items-center mb-3">
                  <i className="fa fa-share-alt text-primary mr-2 font-size-lg"></i>
                  <h6 className="font-weight-bold mb-0 text-dark">Social Media & Online Presence</h6>
                </div>
                <div className="form-group row">
                  <div className="col-lg-4">
                    <Field
                      name="website"
                      component={Input}
                      placeholder="https://example.com"
                      label="Website"
                    />
                  </div>
                  <div className="col-lg-4">
                    <Field
                      name="linkedin"
                      component={Input}
                      placeholder="https://linkedin.com/in/..."
                      label="LinkedIn"
                    />
                  </div>
                  <div className="col-lg-4">
                    <Field
                      name="facebook"
                      component={Input}
                      placeholder="https://facebook.com/..."
                      label="Facebook"
                    />
                  </div>
                </div>

                <div className="form-group row mb-0">
                  <div className="col-lg-4">
                    <Field
                      name="instagram"
                      component={Input}
                      placeholder="https://instagram.com/..."
                      label="Instagram"
                    />
                  </div>
                  <div className="col-lg-4">
                    <Field
                      name="tiktok"
                      component={Input}
                      placeholder="https://tiktok.com/@..."
                      label="TikTok"
                    />
                  </div>
                  <div className="col-lg-4">
                    <Field
                      name="x"
                      component={Input}
                      placeholder="https://x.com/..."
                      label="X (formerly Twitter)"
                    />
                  </div>
                </div>
              </div>

              {/* Categories Section */}
              <div className="card card-custom p-4 mb-6" style={{ backgroundColor: "#ffffff", border: "1px solid #ebedf3", borderRadius: "8px" }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-tags text-primary mr-2 font-size-lg"></i>
                    <h6 className="font-weight-bold mb-0 text-dark">Business Categories</h6>
                  </div>
                  <button
                    type="button"
                    className="btn btn-xs btn-light-primary font-weight-bold"
                    onClick={() => {
                      setCategoryModalInitialName("");
                      setShowAddCategoryModal(true);
                    }}
                  >
                    <i className="fa fa-plus mr-1"></i> Add Category
                  </button>
                </div>
                <CreatableSelect
                  isMulti
                  className="basic-multi-select"
                  classNamePrefix="select"
                  options={categories.map((c) => ({ value: String(c.id), label: c.name }))}
                  value={categories
                    .filter((c) => selectedCategories.includes(String(c.id)))
                    .map((c) => ({ value: String(c.id), label: c.name }))}
                  onChange={(selected) => {
                    setSelectedCategories(selected ? selected.map((s) => s.value) : []);
                  }}
                  onCreateOption={(inputValue) => {
                    setCategoryModalInitialName(inputValue);
                    setShowAddCategoryModal(true);
                  }}
                  placeholder="Select or type new category name..."
                  formatCreateLabel={(inputValue) => `+ Create new category "${inputValue}"...`}
                />
              </div>

              {/* Origins of Country & Spoken Languages */}
              <div className="card card-custom p-4 mb-6" style={{ backgroundColor: "#ffffff", border: "1px solid #ebedf3", borderRadius: "8px" }}>
                <div className="d-flex align-items-center mb-3">
                  <i className="fa fa-globe text-primary mr-2 font-size-lg"></i>
                  <h6 className="font-weight-bold mb-0 text-dark">Origin & Language Support</h6>
                </div>
                <div className="form-group row mb-0">
                  <div className="col-lg-6 mb-3 mb-lg-0">
                    <label className="font-weight-bold">Origins of Country (Multiple Selection)</label>
                    <Select
                      isMulti
                      className="basic-multi-select"
                      classNamePrefix="select"
                      isClearable={true}
                      isSearchable={true}
                      options={countries.map((c) => ({ value: c.name, label: c.name }))}
                      value={selectedOriginCountries.map((c) => ({ value: c, label: c }))}
                      onChange={(selected) => {
                        setSelectedOriginCountries(selected ? selected.map((s) => s.value) : []);
                      }}
                      placeholder="Select origins of country (e.g. Pakistan, India, UK)..."
                    />
                  </div>
                  <div className="col-lg-6">
                    <label className="font-weight-bold">Spoken / Supported Languages (Multiple Selection)</label>
                    <Select
                      isMulti
                      className="basic-multi-select"
                      classNamePrefix="select"
                      isClearable={true}
                      isSearchable={true}
                      options={STANDARD_LANGUAGES}
                      value={selectedLanguages.map((l) => {
                        const found = STANDARD_LANGUAGES.find((sl) => sl.value === l);
                        return found || { value: l, label: l };
                      })}
                      onChange={(selected) => {
                        setSelectedLanguages(selected ? selected.map((s) => s.value) : []);
                      }}
                      placeholder="Select spoken languages (e.g. English, Urdu, Arabic)..."
                    />
                  </div>
                </div>
              </div>

              {/* Show Exact Address & Coordinates Toggle */}
              <div className="form-group row mb-6">
                <div className="col-lg-12">
                  <div className="card bg-light-primary border-primary border-dashed p-4" style={{ borderRadius: "8px" }}>
                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                      <div className="mr-3 mb-2 mb-md-0">
                        <h6 className="font-weight-bold text-dark mb-1">
                          <i className="fa fa-map-marker-alt text-primary mr-2"></i>
                          Show Exact Address & Coordinates Publicly
                        </h6>
                        <span className="text-muted font-size-sm">
                          {values.show_exact_address
                            ? "Enabled: Exact street address and coordinates (latitude/longitude) are publicly visible on the website and app."
                            : "Disabled: Only City and Country are displayed. Exact address and coordinates are hidden for privacy."}
                        </span>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className={`label label-lg label-inline font-weight-bold mr-3 ${values.show_exact_address ? "label-light-success" : "label-light-danger"}`}>
                          {values.show_exact_address ? "Public Coordinates" : "Hidden Coordinates"}
                        </span>
                        <span className="switch switch-sm switch-icon switch-success">
                          <label className="mb-0">
                            <input
                              type="checkbox"
                              name="show_exact_address"
                              checked={Boolean(values.show_exact_address)}
                              onChange={(e) => setFieldValue("show_exact_address", e.target.checked)}
                            />
                            <span></span>
                          </label>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                style={{ display: "none" }}
                ref={btnRef}
                onSubmit={() => handleSubmit()}
              ></button>
            </Form>
          </>
        )}
      </Formik>

      <AddCategoryModal
        show={showAddCategoryModal}
        onHide={() => setShowAddCategoryModal(false)}
        onCategoryCreated={handleCategoryFromModal}
        categories={categories}
        initialCategoryName={categoryModalInitialName}
        mode="business"
      />
    </>
  );
}